import React from "react";
import { TRequestParams, createQS } from "./api-utils";
import {
  del as httpDel,
  get as httpGet,
  patch as httpPatch,
  post as httpPost,
  put as httpPut,
  Request,
} from "superagent";
import Storage from "./Storage";
import { TToken } from "./interfaces";
import { IUserInfo } from "interfaces";
import { LoginData } from "stores/UserStore";

interface IAuthClientOptions {
  auth_url: string;
  authData?: IAuthData;
  authContentType?: string;
  authHeader?: string;
}

interface IAuthData {
  client_id?: string;
  scope: string | undefined;
}

export class AuthClient {
  private auth_url: string;

  private storage: Storage;
  private activeAuth: Promise<any> | undefined;

  authData: IAuthData | {};
  authContentType: string;
  authHeader: string;

  constructor(options: IAuthClientOptions) {
    this.auth_url = options.auth_url;

    this.authHeader = options.authHeader || "";

    this.authData = options.authData || {};

    this.authContentType = options.authContentType || "application/json";

    this.storage = new Storage();
  }

  private addHeader =
    (name: string, value: string): ((request: Request) => Request) =>
    (request) =>
      request.set(name, value);

  private addAuthHeader = (req: Request) => {
    const authHeader = this.storage.getToken() ? `Bearer ${this.storage.getToken()}` : this.authHeader;

    return this.addHeader("Authorization", authHeader)(req);
  };

  private addBody =
    (data: any): ((request: Request) => Request) =>
    (request) =>
      request.send(data);

  private addJSONHeader = (request: Request) => {
    this.addHeader("Accept", "application/json")(request);
    this.addHeader("Content-Type", "application/json")(request);

    return request;
  };

  private addContentTypeHeader = (request: Request) => {
    this.addHeader("Accept", this.authContentType)(request);
    this.addHeader("Content-Type", this.authContentType)(request);

    return request;
  };

  private createMethod = (httpMethod: (url: string, callback?: React.FC) => any) => {
    const method = <T>(url: string, params?: any, contentHeader?: any): Promise<T> => {
      return this.authWrapper(() => {
        const req = httpMethod(url);

        if (!(params instanceof FormData)) {
          const addHeader = contentHeader || this.addJSONHeader;
          addHeader(req);
        }

        this.addAuthHeader(req);
        this.addBody(params)(req);

        return req;
      });
    };

    return method;
  };

  public loginOtp = (url: string, { login, password }: { login: string; password: string }): Promise<TToken | null> => {
    return this.post<any>(url, {
      login,
      password,
    });
  };

  public login = (data: LoginData): Promise<IUserInfo | null> => {
    const url = this.auth_url;

    return this.post<TToken>(
      url,
      {
        ...this.authData,
        login: data.user,
        otp: data.smsCode,
        companyId: data.companyId,
        grant_type: "otp",
      },
      this.addContentTypeHeader
    ).then((res) => this.storage.saveToken(res));
  };

  public logout = () => {
    this.storage.dropToken();

    return Promise.resolve();
  };

  public dropToken = () => {
    this.storage.dropToken();
  };

  // Функция для определения сетевых ошибок
  private isNetworkError(error: any): boolean {
    if (!error) return false;

    // Проверяем сообщение об ошибке на наличие ключевых фраз сетевых ошибок
    const errorMessage = String(error.message || "").toLowerCase();
    const networkErrorPhrases = [
      "request has been terminated",
      "network is offline",
      "failed to fetch",
      "network error",
      "connection refused",
      "connection timeout",
      "no internet connection",
      "offline",
      "network request failed",
    ];

    return networkErrorPhrases.some((phrase) => errorMessage.includes(phrase));
  }

  // Функция для создания нормализованной сетевой ошибки
  private createNetworkError(): any {
    return {
      code: "NETWORK_ERROR",
      status: null,
      message: "Отсутствует соединение",
      detail: "Отсутствует соединение с интернетом",
      instance: "",
      title: "Отсутствует соединение",
      type: "network_error",
    };
  }

  public authWrapper = async <T>(method: () => Request): Promise<T> => {
    await this.auth();

    try {
      const res = await method();
      return res.body;
    } catch (e: any) {
      // Проверяем, является ли это сетевой ошибкой
      if (this.isNetworkError(e)) {
        return Promise.reject(this.createNetworkError());
      }

      if (e?.status === 401) {
        this.storage.clearAuthToken();
        await this.auth();

        try {
          const res = await method();
          return res.body;
        } catch (error: any) {
          // Проверяем, является ли это сетевой ошибкой во второй попытке
          if (this.isNetworkError(error)) {
            return Promise.reject(this.createNetworkError());
          }

          if (error?.status === 401) {
            this.logout();
            window.location.reload();
            return Promise.reject(error);
          } else {
            return Promise.reject(error);
          }
        }
      } else {
        // В этом варианте нет полей статус и type в содержимом ошибки
        // throw new Error(e);
        return Promise.reject(e);
      }
    }
  };

  private getRequest = (url: string, params?: TRequestParams): Request => {
    const urlWithQuery = params ? `${url}?${createQS(params)}` : url;
    const req = httpGet(urlWithQuery);
    this.addJSONHeader(req);
    this.addAuthHeader(req);

    return req;
  };

  public get = <T>(url: string, params?: TRequestParams): Promise<T> =>
    this.authWrapper(() => this.getRequest(url, params));

  public refreshToken = (): Promise<any> => {
    const refresh_token = this.storage.getRefreshToken();

    if (!refresh_token) {
      return Promise.reject();
    }

    const req = httpPost(this.auth_url);

    this.addContentTypeHeader(req);
    this.addAuthHeader(req);
    this.addBody({
      ...this.authData,
      grant_type: "otp",
      refresh_token,
    })(req);

    return req.then((res) => res.body).then(this.storage.saveToken);
  };

  public auth = (): Promise<void | IUserInfo | null> => {
    if (this.activeAuth) {
      return this.activeAuth;
    }

    if (this.storage.getToken()) {
      return Promise.resolve();
    }

    const refresh_token = this.storage.getRefreshToken();

    if (!refresh_token) {
      return Promise.resolve();
    }

    const req = httpPost(this.auth_url);

    this.addContentTypeHeader(req);
    this.addAuthHeader(req);
    this.addBody({
      ...this.authData,
      grant_type: "otp",
      refresh_token,
    })(req);

    this.activeAuth = req;

    return req
      .then((res) => res.body)
      .then(this.storage.saveToken)
      .catch(() => {
        this.storage.saveToken(null);
      })
      .finally(() => {
        this.activeAuth = undefined;
      });
  };

  public patch = this.createMethod(httpPatch);
  public post = this.createMethod(httpPost);
  public put = this.createMethod(httpPut);
  public del = this.createMethod(httpDel);

  public getToken = () => this.storage.getToken();
  public saveToken = (token: TToken | null) => this.storage.saveToken(token);
  public clearAuthToken = () => this.storage.clearAuthToken();
}
